<template>
    <div
        class="relative inline-block w-full"
        v-click-outside="() => (showCardsList = false)"
    >
        <label v-if="label" class="block w-full mb-2 text-sm font-medium text-slate-800 dark:text-slate-200 leading-tight">
            {{ label }}
        </label>
        <div class="relative w-full">
            <!-- Placeholder card image -->
            <img
                v-if="!currentCard || query"
                src="/images/placeholder-search-card-input.png"
                alt="Card placeholder"
                class="absolute left-3 top-1/2 -translate-y-1/2 w-7 h-6 object-contain pointer-events-none z-10 dark:opacity-40"
            />

            <!-- Input -->
            <input
                type="text"
                ref="searchinput"
                class="h-10 w-full flex items-center gap-2 text-left pr-10 bg-white dark:bg-slate-700 rounded border border-slate-200 dark:border-slate-600 py-1.5 placeholder:text-slate-500 dark:placeholder:text-slate-400 placeholder:text-sm transition-colors text-sm focus:ring-0 focus:border-slate-800 dark:focus:border-slate-400 hover:border-slate-800 dark:hover:border-slate-400 disabled:opacity-50 disabled:cursor-not-allowed"
                :class="[
                    currentCard && !query ? 'text-transparent caret-slate-500' : 'text-slate-800 dark:text-slate-100',
                    (!currentCard || query) ? 'pl-12' : 'pl-3',
                    validationError ? '!border-red-500' : ''
                ]"
                v-model="query"
                :placeholder="currentCard ? '' : placeholder"
                @keydown.enter.prevent="enter"
                @keydown.down="down"
                @keydown.up="up"
                @input="change"
                @focus="activateSearch"
                name="card_search"
                :required="required"
                :disabled="disabled"
                v-debounce="200"
                autocomplete="off"
            />
            
            <!-- Selected card overlay -->
            <span
                v-if="currentCard && !query"
                class="absolute left-3 right-12 top-1/2 -translate-y-1/2 flex items-center gap-2 pointer-events-none z-10"
            >
                <img
                    v-if="currentCard.card_image"
                    :src="`${cdnUrl}/${currentCard.card_image.path}/_small/${currentCard.card_image.file_name}`"
                    :alt="currentCard.title"
                    class="w-8 h-5 rounded-sm object-cover shrink-0"
                />
                <span class="text-slate-800 dark:text-slate-100 text-sm truncate flex-1 min-w-0">
                    {{ currentCard.title }}
                </span>
            </span>

            <!-- Clear button -->
            <button
                v-if="query || currentCard"
                type="button"
                class="absolute right-2 top-1/2 -translate-y-1/2 text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-slate-100 w-4 h-4 z-20"
                @click="clearSearch"
                aria-label="Clear search"
            >
                <Icon icon="close" class="w-3 h-3" />
            </button>
        </div>
        
        <!-- Validation error message -->
        <div v-if="validationError" class="text-red-500 text-xs flex items-center gap-2 py-1.5" role="alert">
            <Icon icon="caution" />
            {{ validationError }}
        </div>

        <Teleport to="body">
            <transition name="fade">
                <div
                    class="flex flex-col p-2 overflow-y-auto bg-white dark:bg-slate-800 rounded-lg shadow-sm dark:shadow-slate-900/50 border border-slate-200 dark:border-slate-700 fixed z-[99999]"
                    :style="positionAbove
                        ? `width: ${width}px; bottom: ${top}px; left: ${left}px; max-height: ${maxHeight}px`
                        : `width: ${width}px; top: ${top}px; left: ${left}px; max-height: ${maxHeight}px`"
                    v-show="showCardsList"
                >
                    <div v-if="groupedResults.length > 0" class="flex flex-col">
                        <div
                            v-for="(group, groupIndex) in groupedResults"
                            :key="'group-' + groupIndex"
                            class="flex flex-col"
                        >
                            <!-- Bank/Institution Header -->
                            <div class="px-3 py-2 text-slate-800 dark:text-slate-200 text-sm font-medium leading-5">
                                {{ group.bankName }}
                            </div>

                            <!-- Cards under this bank -->
                            <div
                                v-for="(card, cardIndex) in group.cards"
                                :key="'card-' + groupIndex + '-' + cardIndex"
                                class="flex items-center gap-2 px-3 py-3 min-h-[44px] hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer rounded"
                                :class="{ 'bg-slate-50 dark:bg-slate-700': card.active }"
                                @click="chooseItem(card)"
                            >
                                <ImageLoader class="shrink-0">
                                    <template #image>
                                        <img
                                            v-if="card.card_image"
                                            :src="`${cdnUrl}/${card.card_image.path}/_small/${card.card_image.file_name}`"
                                            :alt="card.title"
                                            class="w-8 h-5 rounded-sm object-cover"
                                        />
                                    </template>
                                    <template #error>
                                        <Icon
                                            icon="credit-card"
                                            class="text-slate-600 dark:text-slate-300 w-8 h-5"
                                        />
                                    </template>
                                </ImageLoader>
                                <span class="flex-1 text-slate-800 dark:text-slate-100 text-sm font-normal leading-5 truncate">
                                    {{ card.title }}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div v-else class="px-3 py-3 text-center text-slate-500 dark:text-slate-400 text-sm">
                        No cards found
                    </div>
                </div>
            </transition>
        </Teleport>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import Input from '@/components/ui/inputs/ui-input.vue';
import ImageLoader from '@/components/ui/image-loader/image-loader.vue';
import Icon from '@/components/ui/icon/icon.vue';

const props = defineProps({
    required: {
        type: Boolean,
        default: false,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    validationError: {
        type: String,
        default: '',
    },
    institutionCode: {
        type: String,
        default: '',
    },
    label: {
        type: [String, Boolean],
        default: 'Search for card',
    },
    placeholder: {
        type: String,
        default: 'Search for card',
    },
    currentCard: {
        type: [Object, null],
        default: null,
    },
    showInstitution: {
        type: Boolean,
        default: true,
    },
    cardToOmit: {
        type: [Object, null],
        default: null,
    },
});

const emit = defineEmits(['card-selected', 'clear-current-card']);
const modelValue = defineModel('modelValue');

const itemId = ref('');
const query = ref('');
const results = ref([]);
const selected = ref(-1);
const width = ref(0);
const top = ref(0);
const left = ref(0);
const maxHeight = ref(400);
const positionAbove = ref(false);
const searchinput = ref(null);
const cdnUrl = window.cdnUrl;
const showCardsList = ref(false);
const chosenCard = ref(null);

// Computed property to group cards by bank
const groupedResults = computed(() => {
    if (!results.value || results.value.length === 0) return [];
    
    const groups = {};
    
    results.value.forEach(card => {
        const bankName = card.issuing_bank?.name || 'Other';
        
        if (!groups[bankName]) {
            groups[bankName] = {
                bankName: bankName,
                cards: []
            };
        }
        
        groups[bankName].cards.push(card);
    });
    
    // Convert to array and sort by bank name
    return Object.values(groups).sort((a, b) => a.bankName.localeCompare(b.bankName));
});

defineOptions({
    directives: {
        'click-outside': {
            mounted(el, binding) {
                el.clickOutsideEvent = (event) => {
                    if (!(el === event.target || el.contains(event.target))) {
                        binding.value(event);
                    }
                };
                document.addEventListener('click', el.clickOutsideEvent);
            },
            unmounted(el) {
                document.removeEventListener('click', el.clickOutsideEvent);
            },
        },
    },
});

watch(showCardsList, (val) => {
    if (val) {
        setCardsDropdownPosition();
    }
});

function change(e) {
    const searchValue = e.target.value;

    if (chosenCard.value && chosenCard.value.title !== searchValue) {
        chosenCard.value = null;
        itemId.value = '';
        emit('clear-current-card');
    }

    if (searchValue.length >= 2) {
        query.value = searchValue;
        getItems();
    } else {
        results.value = [];
        showCardsList.value = false;
    }
}

function clearCurrentCard() {
    emit('clear-current-card');
}

function clearSearch() {
    query.value = '';
    results.value = [];
    showCardsList.value = false;
    chosenCard.value = null;
    itemId.value = '';
    if (props.currentCard) {
        clearCurrentCard();
    }
}

function activateSearch() {
    if (query.value && query.value.trim().length >= 2) {
        getItems();
    }
}

async function getItems() {
    try {
        if (!query.value || query.value.trim() === '') {
            results.value = [];
            showCardsList.value = false;
            return;
        }

        let items = [];
        const searchQuery = encodeURIComponent(query.value.trim());
        const institutionCode = props.institutionCode || '';

        const response = await window.ApiService.apiCall(
            `/cards/user-search?s=${searchQuery}&institution_code=${institutionCode}`,
            'get'
        );

        if (props.cardToOmit && props.cardToOmit.id) {
            items = response.data.data.filter(
                (item) => item.id !== props.cardToOmit.id
            );
        } else {
            items = response.data.data;
        }

        results.value = items;

        if (results.value.length > 0) {
            results.value[0].active = true;
            selected.value = 0;
        }
        
        // Show dropdown if we have results OR if we searched but found nothing
        showCardsList.value = true;
    } catch (error) {
        console.error('Error fetching cards:', error);
        results.value = [];
        showCardsList.value = true; // Show dropdown with "No cards found" message
    }
}

function chooseItem(item) {
    itemId.value = item.id;
    query.value = '';
    results.value = [];
    showCardsList.value = false;
    chosenCard.value = item;
    emit('card-selected', item);
}

function up() {
    if (results.value && results.value.length > 0 && selected.value >= 0) {
        if (selected.value > 0) {
            selected.value--;
        }
        resetActiveItem();
        if (selected.value >= 0 && selected.value < results.value.length) {
            results.value[selected.value].active = true;
        }
    }
}

function down() {
    if (
        results.value &&
        results.value.length > 0 &&
        selected.value + 1 < results.value.length
    ) {
        selected.value++;
        resetActiveItem();
        if (selected.value >= 0 && selected.value < results.value.length) {
            results.value[selected.value].active = true;
        }
    }
}

function resetActiveItem() {
    results.value.forEach((item) => {
        item.active = false;
    });
}

function enter() {
    // Check if we have valid results and a valid selection
    if (
        results.value &&
        results.value.length > 0 &&
        selected.value >= 0 &&
        selected.value < results.value.length
    ) {
        const item = results.value[selected.value];
        if (item) {
            chooseItem(item);
        }
    }
}

const setCardsDropdownPosition = () => {
    if (!searchinput.value) return;
    const rect = searchinput.value.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    const minSpaceBelow = 200;

    width.value = rect.width;
    left.value = rect.left;

    const margin = 16;
    if (spaceBelow >= minSpaceBelow || spaceBelow >= spaceAbove) {
        positionAbove.value = false;
        top.value = rect.bottom + 4;
        maxHeight.value = Math.max(120, Math.min(400, spaceBelow - margin));
    } else {
        positionAbove.value = true;
        top.value = viewportHeight - rect.top + 4;
        maxHeight.value = Math.max(120, Math.min(400, spaceAbove - margin));
    }
};

// Expose clear method to parent
defineExpose({
    clear() {
        query.value = '';
        itemId.value = '';
        results.value = [];
        selected.value = -1;
        if (searchinput.value) {
            searchinput.value.focus();
        }
    },
});

onMounted(() => {
    setCardsDropdownPosition();
    window.addEventListener('resize', setCardsDropdownPosition);
    window.addEventListener('scroll', setCardsDropdownPosition);
});

onBeforeUnmount(() => {
    window.removeEventListener('resize', setCardsDropdownPosition);
    window.removeEventListener('scroll', setCardsDropdownPosition);
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
